import { Vue, Component, Watch } from 'vue-property-decorator';
import { SearchComponent, SearchServicePlugin, ConfigServicePlugin } from '@samc/search';
import { ICurrentUser } from '@samc/common';
import '@/React/AuthHandler.react'; // Attaches to the "authSection" article below the main one, handles Auth components, and displays the main app when the user is authenticated.
import '@samc/search/dist/style.css';
import { exportLogs, clearLogs } from '@/utils';
import SearchResultNavigationHandler from '@/SearchResultNavigationHandler';
import { NotificationBadge } from '@/Components/Notifications';
import { default as LegacyImportBadge } from '@/Components/LegacyImportBadge.vue';
import { Tooltip, MainMenuItem, SubMenuItem, MenuLink, MainMenu } from '@samc/centricity-core';

const app = document.getElementById('main-content');
const searchServiceOptions = JSON.parse(app?.dataset["searchServiceOptions"] ?? 'null');
const configServiceOptions = JSON.parse(app?.dataset["configServiceOptions"] ?? 'null');

import '@/registerVuePlugins';
Vue.use(SearchServicePlugin, searchServiceOptions);
Vue.use(ConfigServicePlugin, configServiceOptions);

@Component({
  name: 'main-layout',
  components: {
    SearchComponent, NotificationBadge, LegacyImportBadge, Tooltip, MainMenuItem, SubMenuItem, MenuLink, MainMenu
  }
})
 class SharedLayoutComponent extends Vue {
  public showMenu: boolean = false;
  public unpinMenuAnimationDone: boolean = false;
  public unpinMenu: boolean = false;
  public menuUnpinMenu: boolean = false;
  public searchResultNavigationHandler!: SearchResultNavigationHandler;
  public showAccountMenu: boolean = false;
  public searchText: string = '';
  public entitledToViewLoanDetails: boolean = false;
  public timeout: number = 150;
  public stagger: number | NodeJS.Timeout | null = null;
  public verificationToken: string = '';

  public mainContent(): HTMLElement | null { return document.getElementById('main-content'); }
  public mainMenu(): HTMLElement | undefined { return this.$refs.mainMenu as HTMLElement; }
  public mainHeader(): HTMLElement | undefined { return this.$refs.mainHeader as HTMLElement; }
  public logoutForm(): HTMLFormElement | undefined { return this.$refs.logoutForm as HTMLFormElement; }

  public navigateToSearchResult(domain: string, result: any) {
    this.searchResultNavigationHandler.navigateToSearchResult(domain, result);
  }

  public setHovering(value: boolean, e: MouseEvent) {
    if (this.stagger !== null) {
      clearTimeout(this.stagger);
    }

    const hoverClass = 'hover';
    this.stagger = setTimeout(() => {
      const div = e.target as HTMLDivElement | null;
      const mainMenu = this.mainMenu();
      if (div !== null && mainMenu !== undefined) {
        // Remove all other hover classes from items
        const elements = mainMenu.querySelectorAll(`.item.${hoverClass}`);
        for (let i = 0; i < elements.length; ++i) {
          if (elements[i] !== div) {
            elements[i].classList.remove(hoverClass);
          }
        }

        // Process the type of hover we are dealing with
        if (value) {
          if (!div.classList.contains(hoverClass))
            div.classList.add(hoverClass);
        } else {
          if (div.classList.contains(hoverClass))
            div.classList.remove(hoverClass);
        }
      }
      this.stagger = null;
    }, this.timeout);
  }

  public logout() {
    const form = this.logoutForm();
    if (form !== undefined)
      form.submit();
  }

  public async mounted() {
    this.searchResultNavigationHandler = new SearchResultNavigationHandler(this.$loading);
    this.verificationToken = await this.$verificationToken() ?? '';
  }

  public downloadLog() {
    exportLogs();
  }
  public clearLog() {
    clearLogs();
  }

  public toggleUnPinned() {
    const duration = 250;
    const offset = 16;
    this.unpinMenu = !this.unpinMenu;
    this.showMenu = true;
    this.menuUnpinMenu = false;

    const mainMenu = this.mainMenu();
    if (mainMenu !== undefined) {
      if (!this.unpinMenu) {
        mainMenu.classList.remove('un-pinned');
      }

      const start: any = new Date();
      const render = () => {
        const next: any = new Date();
        const delta = next - start;
        if (delta < duration) {
          let percent = (delta / duration) * offset;
          if (!this.unpinMenu) {
            percent = offset - percent;
          }
          mainMenu.style.marginLeft = `-${percent}rem`;

          const mainContent = this.mainContent()
          if (mainContent !== null)
            mainContent.style.paddingLeft = `${offset - percent}rem`;

          // Request another render frame
          window.dispatchEvent(new Event('resize'));
          requestAnimationFrame(render);
        } else {
          if (this.unpinMenu) {
            this.menuUnpinMenu = true;
          }
          mainMenu.style.marginLeft = '';

          const mainContent = this.mainContent();
          if (mainContent !== null)
            mainContent.style.paddingLeft = '';
          this.showMenu = false;
        }
      };

      requestAnimationFrame(render);
    }
  }

  public async onMultipleLoanSearch() {
    window.location.href = '/DataImport/MultipleListSearch';
  }

  @Watch('$user')
  public onUserUpdated(providedUser: ICurrentUser | null) {
    const user = this.$user || providedUser;
    if (user) {
      this.searchResultNavigationHandler.setUserEntitlements(user);
    }
  }

  @Watch('unpinMenu')
  public onPinMenuChange(newUnpinMenu: boolean) {
    const mainContent = this.mainContent();
    if (mainContent !== null) {
      if (!newUnpinMenu) {
        mainContent.classList.add('pl-64');
      } else {
        mainContent.classList.remove('pl-64')
      }
    }
  }
}

new SharedLayoutComponent()
  .$mount('#main-header');